/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

import { AnalyticsActionPayload } from "@/store/analytics-store"

type FlowKey = "C1" | "C1PDV" | "OCR" | "OCRPDV"

type FlowStep = {
  path: string
  nextStepPath?: string
  prevStepPath?: string
}

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const flowC1: FlowStep[] = [
  {
    path: "/codice-fiscale",
    nextStepPath: "/indirizzo-residenza",
    prevStepPath: "/",
  },
  {
    path: "/indirizzo-residenza",
    nextStepPath: "/contatti",
    prevStepPath: "/codice-fiscale",
  },
  {
    path: "/contatti",
    nextStepPath: "/dati-di-accesso",
    prevStepPath: "/indirizzo-residenza",
  },
  {
    path: "/dati-di-accesso",
    nextStepPath: "/bonus",
    prevStepPath: "/contatti",
  },
  {
    path: "/bonus",
    nextStepPath: "/dati-documento",
    prevStepPath: "/dati-di-accesso",
  },
  {
    path: "/dati-documento",
    nextStepPath: "/limiti-ricarica",
    prevStepPath: "/bonus",
  },
  {
    path: "/limiti-ricarica",
    nextStepPath: "/termini-e-condizioni",
    prevStepPath: "/dati-documento",
  },
  {
    path: "/termini-e-condizioni",
    prevStepPath: "/limiti-ricarica",
  },
]

const flowC1Pdv: FlowStep[] = [
  {
    path: "/registrazione-pdv",
    nextStepPath: "/codice-fiscale",
    prevStepPath: "/codice-fiscale",
  },
  {
    path: "/codice-fiscale",
    nextStepPath: "/indirizzo-residenza",
    prevStepPath: "/registrazione-pdv",
  },
  {
    path: "/indirizzo-residenza",
    nextStepPath: "/contatti",
    prevStepPath: "/codice-fiscale",
  },
  {
    path: "/contatti",
    nextStepPath: "/dati-di-accesso",
    prevStepPath: "/indirizzo-residenza",
  },
  {
    path: "/dati-di-accesso",
    nextStepPath: "/bonus",
    prevStepPath: "/contatti",
  },
  {
    path: "/bonus",
    nextStepPath: "/dati-documento",
    prevStepPath: "/dati-di-accesso",
  },
  {
    path: "/dati-documento",
    nextStepPath: "/limiti-ricarica",
    prevStepPath: "/bonus",
  },
  {
    path: "/limiti-ricarica",
    nextStepPath: "/termini-e-condizioni",
    prevStepPath: "/dati-documento",
  },
  {
    path: "/termini-e-condizioni",
    prevStepPath: "/limiti-ricarica",
  },
]

const flowOCR: FlowStep[] = [
  {
    path: "/riepilogo-dati-ocr",
    nextStepPath: "/contatti",
  },
  {
    path: "/contatti",
    nextStepPath: "/dati-di-accesso",
    prevStepPath: "/riepilogo-dati-ocr",
  },
  {
    path: "/dati-di-accesso",
    nextStepPath: "/bonus",
    prevStepPath: "/contatti",
  },
  {
    path: "/bonus",
    nextStepPath: "/limiti-ricarica",
    prevStepPath: "/dati-di-accesso",
  },
  {
    path: "/limiti-ricarica",
    nextStepPath: "/termini-e-condizioni-ocr",
    prevStepPath: "/bonus",
  },
  {
    path: "/termini-e-condizioni-ocr",
    prevStepPath: "/limiti-ricarica",
  },
]

const flowOcrPdv: FlowStep[] = [
  {
    path: "/registrazione-semplificata-pdv/riepilogo-dati-ocr",
    nextStepPath: "/registrazione-semplificata-pdv/contatti",
  },
  {
    path: "/registrazione-semplificata-pdv/contatti",
    nextStepPath: "/registrazione-semplificata-pdv/bonus",
    prevStepPath: "/registrazione-semplificata-pdv/riepilogo-dati-ocr",
  },
  {
    path: "/registrazione-semplificata-pdv/bonus",
    prevStepPath: "/registrazione-semplificata-pdv/contatti",
  },
]

const analyticsPageByFlow: Record<
  FlowKey | "no-flow",
  Record<string, AnalyticsActionPayload>
> = {
  C1: {
    "codice-fiscale": {
      page_name: "registrazione:standard:step_2:codice_fiscale",
      conversion_pagename: "registrazione:standard:step_2:codice_fiscale",
      flusso_registrazione: "ocr",
      calculate_cf_dialog_conversion_pagename:
        "registrazione:standard:step_2:calcolo_codice_fiscale",
    },
    "indirizzo-residenza": {
      page_name: "registrazione:standard:step_3:indirizzo",
      conversion_pagename: "registrazione:standard:step_3:indirizzo",
      flusso_registrazione: "ocr",
    },
    contatti: {
      page_name: "registrazione:standard:step_4:email_cellulare",
      conversion_pagename: "registrazione:standard:step_4:email_cellulare",
      flusso_registrazione: "ocr",
      verify_otp_conversion_pagename:
        "registrazione:standard:step_4:verifica_cel_otp",
    },
    "dati-di-accesso": {
      page_name: "registrazione:standard:step_5:username_password",
      conversion_pagename: "registrazione:standard:step_5:username_password",
      flusso_registrazione: "ocr",
    },
    bonus: {
      page_name: "registrazione:standard:step_6:bonus_di_benvenuto",
      conversion_pagename: "registrazione:standard:step_6:bonus_di_benvenuto",
      flusso_registrazione: "ocr",
      bonus_detail_dialog_conversion_pagename:
        "registrazione:standard:step_6:dettaglio_bonus",
      bonus_code_dialog_conversion_pagename:
        "registrazione:standard:step_6:bonus_di_benvenuto_codice",
    },
    "dati-documento": {
      page_name: "registrazione:standard:step_7:dati_documento",
      conversion_pagename: "registrazione:standard:step_7:dati_documento",
      flusso_registrazione: "ocr",
    },
    "limiti-ricarica": {
      page_name: "registrazione:standard:step_8:limite_ricarica",
      conversion_pagename: "registrazione:standard:step_8:limite_ricarica",
      flusso_registrazione: "ocr",
    },
    "termini-e-condizioni": {
      page_name: "registrazione:standard:step_9:condizioni_privacy",
      conversion_pagename: "registrazione:standard:step_9:condizioni_privacy",
      flusso_registrazione: "ocr",
    },
    "fotografa-documento": {
      page_name: "registrazione:standard:fotografa_documento",
      conversion_pagename: "registrazione:standard:fotografa_documento",
      flusso_registrazione: "ocr",
    },
    thankyou: {
      page_name: "registrazione:standard:typ",
      conversion_pagename: "registrazione:standard:typ",
      flusso_registrazione: "ocr",
    },
    "errore-generico": {
      page_name: "registrazione:standard:errore_generico",
      conversion_pagename: "registrazione:standard:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:standard:errore_auto_esclusione",
      conversion_pagename: "registrazione:standard:errore_auto_esclusione",
      flusso_registrazione: "ocr",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:standard:errore_conto_chiuso",
      conversion_pagename: "registrazione:standard:errore_conto_chiuso",
      flusso_registrazione: "ocr",
    },
    "errore-centenario": {
      page_name: "registrazione:standard:errore_centenario",
      conversion_pagename: "registrazione:standard:errore_centenario",
      flusso_registrazione: "ocr",
    },
    "errore-minore": {
      page_name: "registrazione:standard:errore_minore",
      conversion_pagename: "registrazione:standard:errore_minore",
      flusso_registrazione: "ocr",
    },
    "errore-cf-presente": {
      page_name: "registrazione:standard:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:standard:error_page:conto_gia_aperto",
      flusso_registrazione: "ocr",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:standard:errore_generico",
      conversion_pagename: "registrazione:standard:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:standard:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:standard:errore_conto_aperto-pdv",
      flusso_registrazione: "ocr",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:standard:errore_dati_incongruenti",
      conversion_pagename: "registrazione:standard:errore_dati_incongruenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:standard:errore_no_dipendenti",
      conversion_pagename: "registrazione:standard:errore_no_dipendenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-promo": {
      page_name: "registrazione:standard:errore_no_promo",
      conversion_pagename: "registrazione:standard:errore_no_promo",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:standard:errore_ocr_malfunzionamento",
      conversion_pagename: "registrazione:standard:errore_ocr_malfunzionamento",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:standard:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:standard:errore_ocr_non_supportato",
      flusso_registrazione: "ocr",
    },
  },
  C1PDV: {
    "registrazione-pdv": {
      page_name: "registrazione:pdv:step_0",
      conversion_pagename: "registrazione:pdv:step_0",
      flusso_registrazione: "pdv",
    },
    "codice-fiscale": {
      page_name: "registrazione:pdv:step_3:codice_fiscale",
      conversion_pagename: "registrazione:pdv:step_3:codice_fiscale",
      flusso_registrazione: "pdv",
      calculate_cf_dialog_conversion_pagename:
        "registrazione:pdv:step_3:calcolo_codice_fiscale",
    },
    "indirizzo-residenza": {
      page_name: "registrazione:pdv:step_4:indirizzo",
      conversion_pagename: "registrazione:pdv:step_4:indirizzo",
      flusso_registrazione: "pdv",
    },
    contatti: {
      page_name: "registrazione:pdv:step_5:email_cellulare",
      conversion_pagename: "registrazione:pdv:step_5:email_cellulare",
      flusso_registrazione: "pdv",
      verify_otp_conversion_pagename:
        "registrazione:pdv:step_5:verifica_cel_otp",
    },
    "dati-di-accesso": {
      page_name: "registrazione:pdv:step_6:username_password",
      conversion_pagename: "registrazione:pdv:step_6:username_password",
      flusso_registrazione: "pdv",
    },
    bonus: {
      page_name: "registrazione:pdv:step_7:bonus_di_benvenuto",
      conversion_pagename: "registrazione:pdv:step_7:bonus_di_benvenuto",
      flusso_registrazione: "pdv",
      bonus_detail_dialog_conversion_pagename:
        "registrazione:pdv:step_7:dettaglio_bonus",
      bonus_code_dialog_conversion_pagename:
        "registrazione:pdv:step_7:bonus_di_benvenuto_codice",
    },
    "dati-documento": {
      page_name: "registrazione:pdv:step_8:dati_documento",
      conversion_pagename: "registrazione:pdv:step_8:dati_documento",
      flusso_registrazione: "pdv",
    },
    "limiti-ricarica": {
      page_name: "registrazione:pdv:step_9:limite_ricarica",
      conversion_pagename: "registrazione:pdv:step_9:limite_ricarica",
      flusso_registrazione: "pdv",
    },
    "termini-e-condizioni": {
      page_name: "registrazione:pdv:step_10:condizioni_privacy",
      conversion_pagename: "registrazione:pdv:step_10:condizioni_privacy",
      flusso_registrazione: "pdv",
    },
    thankyou: {
      page_name: "registrazione:pdv:typ",
      conversion_pagename: "registrazione:pdv:typ",
      flusso_registrazione: "pdv",
    },
    "fotografa-documento": {
      page_name: "registrazione:pdv:fotografa_documento",
      conversion_pagename: "registrazione:pdv:fotografa_documento",
      flusso_registrazione: "pdv",
    },
    "errore-generico": {
      page_name: "registrazione:pdv:errore_generico",
      conversion_pagename: "registrazione:pdv:errore_generico",
      flusso_registrazione: "pdv",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:pdv:errore_auto_esclusione",
      conversion_pagename: "registrazione:pdv:errore_auto_esclusione",
      flusso_registrazione: "pdv",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:pdv:errore_conto_chiuso",
      conversion_pagename: "registrazione:pdv:errore_conto_chiuso",
      flusso_registrazione: "pdv",
    },
    "errore-centenario": {
      page_name: "registrazione:pdv:errore_centenario",
      conversion_pagename: "registrazione:pdv:errore_centenario",
      flusso_registrazione: "pdv",
    },
    "errore-minore": {
      page_name: "registrazione:pdv:errore_minore",
      conversion_pagename: "registrazione:pdv:errore_minore",
      flusso_registrazione: "pdv",
    },
    "errore-cf-presente": {
      page_name: "registrazione:pdv:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:pdv:error_page:conto_gia_aperto",
      flusso_registrazione: "pdv",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:pdv:errore_generico",
      conversion_pagename: "registrazione:pdv:errore_generico",
      flusso_registrazione: "pdv",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:pdv:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:pdv:errore_conto_aperto-pdv",
      flusso_registrazione: "pdv",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:pdv:errore_dati_incongruenti",
      conversion_pagename: "registrazione:pdv:errore_dati_incongruenti",
      flusso_registrazione: "pdv",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:pdv:errore_no_dipendenti",
      conversion_pagename: "registrazione:pdv:errore_no_dipendenti",
      flusso_registrazione: "pdv",
    },
    "errore-no-promo": {
      page_name: "registrazione:pdv:errore_no_promo",
      conversion_pagename: "registrazione:pdv:errore_no_promo",
      flusso_registrazione: "pdv",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:pdv:errore_ocr_malfunzionamento",
      conversion_pagename: "registrazione:pdv:errore_ocr_malfunzionamento",
      flusso_registrazione: "pdv",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:pdv:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:pdv:errore_ocr_non_supportato",
      flusso_registrazione: "pdv",
    },
  },
  OCR: {
    "seleziona-documento": {
      page_name: "registrazione:ocr:step_1:seleziona_documento",
      conversion_pagename: "registrazione:ocr:step_1:seleziona_documento",
      flusso_registrazione: "ocr",
    },
    "fotografa-documento": {
      page_name: "registrazione:ocr:fotografa_documento",
      conversion_pagename: "registrazione:ocr:fotografa_documento",
      flusso_registrazione: "ocr",
    },
    "riepilogo-dati-ocr": {
      page_name: "registrazione:ocr:step_5:verifica_dati",
      conversion_pagename: "registrazione:ocr:step_5:verifica_dati",
      flusso_registrazione: "ocr",
    },
    contatti: {
      page_name: "registrazione:ocr:step_6:email_cellulare",
      conversion_pagename: "registrazione:ocr:step_6:email_cellulare",
      flusso_registrazione: "ocr",
    },
    "dati-di-accesso": {
      page_name: "registrazione:ocr:step_7:username_password",
      conversion_pagename: "registrazione:ocr:step_7:username_password",
      flusso_registrazione: "ocr",
    },
    bonus: {
      page_name: "registrazione:ocr:step_8:bonus_di_benvenuto",
      conversion_pagename: "registrazione:ocr:step_8:bonus_di_benvenuto",
      flusso_registrazione: "ocr",
      bonus_detail_dialog_conversion_pagename:
        "registrazione:ocr:step_8:dettaglio_bonus",
      bonus_code_dialog_conversion_pagename:
        "registrazione:ocr:step_8:bonus_di_benvenuto_codice",
    },
    "limiti-ricarica": {
      page_name: "registrazione:ocr:step_9:limite_ricarica",
      conversion_pagename: "registrazione:ocr:step_9:limite_ricarica",
      flusso_registrazione: "ocr",
    },
    "termini-e-condizioni-ocr": {
      page_name: "registrazione:ocr:step_10:condizioni_privacy",
      conversion_pagename: "registrazione:ocr:step_10:condizioni_privacy",
      flusso_registrazione: "ocr",
    },
    "errore-generico": {
      page_name: "registrazione:ocr:errore_generico",
      conversion_pagename: "registrazione:ocr:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:ocr:errore_auto_esclusione",
      conversion_pagename: "registrazione:ocr:errore_auto_esclusione",
      flusso_registrazione: "ocr",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:ocr:errore_conto_chiuso",
      conversion_pagename: "registrazione:ocr:errore_conto_chiuso",
      flusso_registrazione: "ocr",
    },
    "errore-centenario": {
      page_name: "registrazione:ocr:errore_centenario",
      conversion_pagename: "registrazione:ocr:errore_centenario",
      flusso_registrazione: "ocr",
    },
    "errore-minore": {
      page_name: "registrazione:ocr:errore_minore",
      conversion_pagename: "registrazione:ocr:errore_minore",
      flusso_registrazione: "ocr",
    },
    "errore-cf-presente": {
      page_name: "registrazione:ocr:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:ocr:error_page:conto_gia_aperto",
      flusso_registrazione: "ocr",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:ocr:errore_generico",
      conversion_pagename: "registrazione:ocr:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:ocr:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:ocr:errore_conto_aperto-pdv",
      flusso_registrazione: "ocr",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:ocr:errore_dati_incongruenti",
      conversion_pagename: "registrazione:ocr:errore_dati_incongruenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:ocr:errore_no_dipendenti",
      conversion_pagename: "registrazione:ocr:errore_no_dipendenti",
      flusso_registrazione: "ocr",
    },
    "errore-no-promo": {
      page_name: "registrazione:ocr:errore_no_promo",
      conversion_pagename: "registrazione:ocr:errore_no_promo",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:errore_generico",
      conversion_pagename: "registrazione:errore_generico",
      flusso_registrazione: "ocr",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:ocr:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:ocr:errore_ocr_non_supportato",
      flusso_registrazione: "ocr",
    },
    thankyou: {
      page_name: "registrazione:ocr:typ",
      conversion_pagename: "registrazione:ocr:typ",
      flusso_registrazione: "ocr",
    },
  },
  OCRPDV: {
    "registrazione-pdv": {
      page_name: "registrazione:pdv:step_0:apri_un_conto_rivenditore",
      conversion_pagename: "registrazione:pdv:step_0:apri_un_conto_rivenditore",
      flusso_registrazione: "pdv rivenditori",
    },
    "seleziona-documento": {
      page_name: "registrazione:pdvocr:step_1:seleziona_documento",
      conversion_pagename: "registrazione:pdvocr:step_1:seleziona_documento",
      flusso_registrazione: "ocr rivenditori",
    },
    "fotografa-documento": {
      page_name: "registrazione:pdvocr:step_2:fotografa_documento",
      conversion_pagename: "registrazione:pdvocr:step_2:fotografa_documento",
      flusso_registrazione: "ocr rivenditori",
    },
    "riepilogo-dati-ocr": {
      page_name: "registrazione:pdvocr:step_5:verifica_dati",
      conversion_pagename: "registrazione:pdvocr:step_5:verifica_dati",
      flusso_registrazione: "ocr rivenditori",
    },
    "codice-fiscale": {
      page_name: "registrazione:pdvocr:step_2:codice_fiscale",
      conversion_pagename: "registrazione:pdvocr:step_2:codice_fiscale",
      flusso_registrazione: "ocr rivenditori",
    },
    "indirizzo-residenza": {
      page_name: "registrazione:pdvocr:step_3:indirizzo",
      conversion_pagename: "registrazione:pdvocr:step_3:indirizzo",
      flusso_registrazione: "ocr rivenditori",
    },
    contatti: {
      page_name: "registrazione:pdvocr:step_4:email_cellulare",
      conversion_pagename: "registrazione:pdvocr:step_4:email_cellulare",
      flusso_registrazione: "ocr rivenditori",
    },
    "dati-documento": {
      page_name: "registrazione:pdvocr:step_5:dati_documento",
      conversion_pagename: "registrazione:pdvocr:step_5:dati_documento",
      flusso_registrazione: "ocr rivenditori",
    },
    bonus: {
      page_name: "registrazione:pdvocr:step_6:bonus_di_benvenuto",
      conversion_pagename: "registrazione:pdvocr:step_6:bonus_di_benvenuto",
      flusso_registrazione: "ocr rivenditori",
    },
    thankyou: {
      page_name: "registrazione:pdvocr:typ",
      conversion_pagename: "registrazione:pdvocr:typ",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-generico": {
      page_name: "registrazione:pdvocr:errore_generico",
      conversion_pagename: "registrazione:pdvocr:errore_generico",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-auto-esclusione": {
      page_name: "registrazione:pdvocr:errore_auto_esclusione",
      conversion_pagename: "registrazione:pdvocr:errore_auto_esclusione",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-conto-chiuso": {
      page_name: "registrazione:pdvocr:errore_conto_chiuso",
      conversion_pagename: "registrazione:pdvocr:errore_conto_chiuso",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-centenario": {
      page_name: "registrazione:pdvocr:errore_centenario",
      conversion_pagename: "registrazione:pdvocr:errore_centenario",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-minore": {
      page_name: "registrazione:pdvocr:errore_minore",
      conversion_pagename: "registrazione:pdvocr:errore_minore",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-cf-presente": {
      page_name: "registrazione:pdvocr:error_page:conto_gia_aperto",
      conversion_pagename: "registrazione:pdvocr:error_page:conto_gia_aperto",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-cf-multiaccount": {
      page_name: "registrazione:pdvocr:errore_generico",
      conversion_pagename: "registrazione:pdvocr:errore_generico",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-conto-aperto-pdv": {
      page_name: "registrazione:pdvocr:errore_conto_aperto-pdv",
      conversion_pagename: "registrazione:pdvocr:errore_conto_aperto-pdv",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-dati-incongruenti": {
      page_name: "registrazione:pdvocr:errore_dati_incongruenti",
      conversion_pagename: "registrazione:pdvocr:errore_dati_incongruenti",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-no-dipendenti": {
      page_name: "registrazione:pdvocr:errore_no_dipendenti",
      conversion_pagename: "registrazione:pdvocr:errore_no_dipendenti",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-no-promo": {
      page_name: "registrazione:pdvocr:errore_no_promo",
      conversion_pagename: "registrazione:pdvocr:errore_no_promo",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-ocr-malfunzionamento": {
      page_name: "registrazione:pdvocr:errore_ocr_malfunzionamento",
      conversion_pagename: "registrazione:pdvocr:errore_ocr_malfunzionamento",
      flusso_registrazione: "ocr rivenditori",
    },
    "errore-ocr-non-supportato": {
      page_name: "registrazione:pdvocr:errore_ocr_non_supportato",
      conversion_pagename: "registrazione:pdvocr:errore_ocr_non_supportato",
      flusso_registrazione: "ocr rivenditori",
    },
  },
  "no-flow": {
    pagina_di_disambiguazione: {
      page_name: "pagina_di_disambiguazione",
      conversion_pagename: "pagina_di_disambiguazione",
      flusso_registrazione: "no-flow",
    },
    "errore-generico": {
      page_name: "registrazione:errore_generico",
      conversion_pagename: "registrazione:errore_generico",
    },
  },
}

//VERRA' SPOSTATO IN POSTO PIU APPROPRIATI QUANDO RIFARO STA COSA
const analyticsFlowNameByActualFlowName = {
  C1: "ocr",
  C1PDV: "pdv",
  OCR: "ocr",
  OCRPDV: "pdvocr",
  "no-flow": "no-flow",
}

const flowsMapping = {
  C1: flowC1,
  C1PDV: flowC1Pdv,
  OCR: flowOCR,
  OCRPDV: flowOcrPdv,
}

export { analyticsFlowNameByActualFlowName, analyticsPageByFlow, flowsMapping }
export type { FlowKey }
